import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Phone, Clock, ArrowLeft, Calendar, User, Tag, ChevronRight, Share2 } from "lucide-react";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [config, setConfig] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/blog/${slug}`).then(res => res.json()),
      fetch(`${API_URL}/api/config`).then(res => res.json()),
      fetch(`${API_URL}/api/blog?limit=4`).then(res => res.json())
    ])
      .then(([postData, configData, postsData]) => {
        setPost(postData);
        setConfig(configData);
        setRelatedPosts(postsData.filter(p => p.slug !== slug).slice(0, 3));
        setLoading(false);
      })
      .catch(console.error);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0f2744] mb-4">Article non trouvé</h1>
          <Link to="/blog" className="btn-primary">
            Voir tous les articles
          </Link>
        </div>
      </div>
    );
  }

  // Parse markdown-like content to JSX
  const renderContent = (content) => {
    return content.split('\n').map((line, index) => {
      // H2 headers
      if (line.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-bold text-[#0f2744] mt-8 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
      }
      // H3 headers
      if (line.startsWith('### ')) {
        return (
          <h3 key={index} className="text-xl font-bold text-[#0f2744] mt-6 mb-3">
            {line.replace('### ', '')}
          </h3>
        );
      }
      // Bold text blocks
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <p key={index} className="font-bold text-[#0f2744] my-4">
            {line.replace(/\*\*/g, '')}
          </p>
        );
      }
      // List items
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="ml-6 mb-2 text-gray-600 list-disc">
            {line.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}
          </li>
        );
      }
      // Table (simplified rendering)
      if (line.startsWith('|')) {
        return null; // Skip tables for now, can enhance later
      }
      // Empty lines
      if (!line.trim()) {
        return <div key={index} className="h-4"></div>;
      }
      // Regular paragraphs
      return (
        <p key={index} className="text-gray-600 mb-4 leading-relaxed">
          {line}
        </p>
      );
    });
  };

  return (
    <div data-testid="blog-detail-page">
      {/* Hero */}
      <HeroBanner
        title={post.title}
        subtitle={post.excerpt}
        highlightText="Fichet"
        showFeatures={false}
        showCTA={false}
        config={config}
        variant="compact"
        breadcrumb={
          <div className="flex items-center gap-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white capitalize">{post.category}</span>
          </div>
        }
      />

      {/* Article Content */}
      <section className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <article className="lg:col-span-2">
              {/* Meta info */}
              <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(post.published_at).toLocaleDateString('fr-FR', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </span>
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {post.read_time} min de lecture
                </span>
                <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-medium capitalize">
                  {post.category}
                </span>
              </div>

              {/* Featured Image */}
              <img
                src={post.image_url}
                alt={post.title}
                className="w-full h-64 md:h-80 object-cover rounded-2xl mb-8"
              />

              {/* Content */}
              <div className="prose prose-lg max-w-none">
                {renderContent(post.content)}
              </div>

              {/* Keywords */}
              {post.keywords && post.keywords.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-500">Mots-clés :</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {post.keywords.map((keyword, index) => (
                      <span 
                        key={index}
                        className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Share */}
              <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-between">
                <span className="text-gray-500 text-sm">Partager cet article :</span>
                <div className="flex gap-2">
                  <button 
                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                    className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                  >
                    <span className="text-sm font-bold">f</span>
                  </button>
                  <button 
                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`, '_blank')}
                    className="w-10 h-10 bg-sky-500 text-white rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                  >
                    <span className="text-sm font-bold">X</span>
                  </button>
                  <button 
                    onClick={() => navigator.clipboard.writeText(window.location.href)}
                    className="w-10 h-10 bg-gray-600 text-white rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              {/* CTA Card */}
              <div className="bg-gradient-to-br from-[#0f2744] to-[#1a3a5c] text-white rounded-2xl p-6 mb-6 sticky top-24">
                <h3 className="text-xl font-bold mb-4">
                  Besoin d'aide ?
                </h3>
                <p className="text-gray-300 text-sm mb-6">
                  Nos serruriers experts Fichet sont disponibles 24h/24 pour vous dépanner.
                </p>
                
                {config && (
                  <a
                    href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
                    className="btn-cta w-full flex items-center justify-center gap-2 mb-4"
                    data-testid="blog-sidebar-phone"
                  >
                    <Phone className="w-5 h-5" />
                    {config.phone}
                  </a>
                )}
                
                <Link
                  to="/contact"
                  className="btn-outline w-full border-white text-white hover:bg-white hover:text-[#0f2744] flex items-center justify-center"
                >
                  Demander un devis
                </Link>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="bg-gray-50 rounded-2xl p-6">
                  <h3 className="font-bold text-[#0f2744] mb-4">
                    Articles similaires
                  </h3>
                  <div className="space-y-4">
                    {relatedPosts.map((relatedPost) => (
                      <Link
                        key={relatedPost.id}
                        to={`/blog/${relatedPost.slug}`}
                        className="block group"
                      >
                        <div className="flex gap-3">
                          <img
                            src={relatedPost.image_url}
                            alt={relatedPost.title}
                            className="w-20 h-16 object-cover rounded-lg flex-shrink-0"
                          />
                          <div>
                            <h4 className="text-sm font-semibold text-[#0f2744] group-hover:text-orange-600 transition-colors line-clamp-2">
                              {relatedPost.title}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {relatedPost.read_time} min
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-1 text-orange-600 text-sm font-medium mt-4 hover:gap-2 transition-all"
                  >
                    Tous les articles
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </aside>
          </div>
        </div>
      </section>

      {/* Schema.org Article structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "image": post.image_url,
          "author": {
            "@type": "Organization",
            "name": post.author
          },
          "publisher": {
            "@type": "Organization",
            "name": "Fichet Dépannage",
            "logo": {
              "@type": "ImageObject",
              "url": "https://fichet-depannage.fr/logo.png"
            }
          },
          "datePublished": post.published_at,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": window.location.href
          }
        })
      }} />
    </div>
  );
}
